"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

interface MobileFriendlyTooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  className?: string
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  sideOffset?: number
  shift?: "left" | "right" | "none" // Nouvelle prop pour indiquer la direction du décalage
}

export function MobileFriendlyTooltip({
  content,
  children,
  className,
  side = "top",
  align = "center",
  sideOffset = 4,
  shift = "none", // Par défaut, pas de décalage
}: MobileFriendlyTooltipProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(false)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Détecte si l'appareil est mobile
  React.useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.matchMedia("(hover: none), (pointer: coarse)").matches)
    }

    checkIfMobile()
    window.addEventListener("resize", checkIfMobile)

    return () => {
      window.removeEventListener("resize", checkIfMobile)
    }
  }, [])

  const handleMouseEnter = () => {
    if (!isMobile) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      setIsOpen(true)
    }
  }

  const handleMouseLeave = () => {
    if (!isMobile) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false)
      }, 200)
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    if (isMobile) {
      e.preventDefault()
      e.stopPropagation()
      setIsOpen(!isOpen)
    }
  }

  // Ferme le tooltip lors d'un clic à l'extérieur
  React.useEffect(() => {
    if (!isMobile || !isOpen) return

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node
      const tooltipTrigger = document.querySelector("[data-state='open']")

      if (tooltipTrigger && !tooltipTrigger.contains(target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("click", handleOutsideClick)

    return () => {
      document.removeEventListener("click", handleOutsideClick)
    }
  }, [isMobile, isOpen])

  return (
    <TooltipPrimitive.Provider>
      <TooltipPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
        <TooltipPrimitive.Trigger
          asChild
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            shift === "right" && "tooltip-shift-right", // Ajoute la classe si shift est "right"
            shift === "left" && "tooltip-shift-left", // Ajoute la classe si shift est "left"
            className,
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}

// Injecter les styles dans le document
if (typeof document !== "undefined") {
  if (!document.getElementById("tooltip-shift-style")) {
    const styleElement = document.createElement("style")
    styleElement.id = "tooltip-shift-style"
    styleElement.innerHTML = `
      .tooltip-shift-right {
        transform: translateX(30px);
      }
      .tooltip-shift-left {
        transform: translateX(-30px);
      }
    `
    document.head.appendChild(styleElement)
  }
}

